import React from 'react';
import { HeatmapCell } from '../../types';

interface GapPerformanceHeatmapProps {
  data: HeatmapCell[];
}

export const GapPerformanceHeatmap: React.FC<GapPerformanceHeatmapProps> = ({ data }) => {
  const uniqueXValues = Array.from(new Set(data.map(d => d.x)));
  const uniqueYValues = Array.from(new Set(data.map(d => d.y)));

  const getValue = (x: string, y: string): HeatmapCell | undefined => {
    return data.find(d => d.x === x && d.y === y);
  };

  const allValues = data.map(d => d.value).filter(v => isFinite(v));
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);

  const getColorIntensity = (value: number): string => {
    if (!isFinite(value)) return 'bg-gray-800';

    const normalized = (value - minValue) / (maxValue - minValue);

    if (normalized < 0.2) return 'bg-blue-900/40';
    if (normalized < 0.4) return 'bg-blue-700/60';
    if (normalized < 0.6) return 'bg-yellow-600/60';
    if (normalized < 0.8) return 'bg-orange-600/70';
    return 'bg-red-600/80';
  };

  const getTextColor = (value: number): string => {
    if (!isFinite(value)) return 'text-gray-500';

    const normalized = (value - minValue) / (maxValue - minValue);

    if (normalized < 0.2) return 'text-blue-300';
    if (normalized < 0.4) return 'text-blue-200';
    if (normalized < 0.6) return 'text-yellow-200';
    if (normalized < 0.8) return 'text-orange-200';
    return 'text-red-200';
  };

  return (
    <div className="premium-chart-card">
      <div className="mb-6">
        <h4 className="premium-chart-title">Heatmap Gap % vs Metriche Performance</h4>
        <p className="premium-chart-subtitle">Valori più alti indicano performance migliori (eccetto Chiusure Rosse)</p>
      </div>

      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          <div className="grid" style={{ gridTemplateColumns: `140px repeat(${uniqueXValues.length}, minmax(80px, 1fr))` }}>
            <div className="p-2"></div>
            {uniqueXValues.map((x, index) => (
              <div key={index} className="p-2 text-center">
                <span className="text-xs font-medium text-gray-300">{x}</span>
              </div>
            ))}

            {uniqueYValues.map((y, yIndex) => (
              <React.Fragment key={yIndex}>
                <div className="p-2 flex items-center">
                  <span className="text-xs font-medium text-gray-300">{y}</span>
                </div>
                {uniqueXValues.map((x, xIndex) => {
                  const cell = getValue(x, y);
                  const value = cell?.value ?? 0;
                  const count = cell?.count ?? 0;

                  return (
                    <div
                      key={xIndex}
                      className={`p-3 border border-gray-700 transition-all hover:scale-105 hover:z-10 hover:shadow-lg cursor-pointer ${getColorIntensity(value)}`}
                      title={`${y} per ${x}\nValore: ${value.toFixed(1)}\nGap: ${count}`}
                    >
                      <div className="text-center">
                        <p className={`text-xs font-bold ${getTextColor(value)}`}>
                          {value.toFixed(1)}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          n={count}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-700">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">Legenda Intensità</span>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div className="w-4 h-4 bg-blue-900/40 rounded"></div>
              <span className="text-xs text-gray-400">Bassa</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-4 h-4 bg-yellow-600/60 rounded"></div>
              <span className="text-xs text-gray-400">Media</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-4 h-4 bg-red-600/80 rounded"></div>
              <span className="text-xs text-gray-400">Alta</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
